<template>
  <div class="payment-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <p class="mt-4 text-h6">Carregando dados do pagamento...</p>
      </div>
    </div>

    <!-- Status Failed/Cancelled/Invalid - Tela Modernizada -->
    <div v-else-if="paylink && paylink.is_invalid" class="failed-container">
      <div class="failed-content">
        <!-- Header com logo -->
        <div class="failed-header">
          <div class="company-info">
            <img
              v-if="paylink.system && paylink.system.logo_url"
              :src="paylink.system.logo_url"
              :alt="paylink.account.fantasy_name || paylink.account.name"
              class="company-logo"
            >
            <h2 class="company-name">
              {{ paylink.account.fantasy_name || paylink.account.name }}
            </h2>
          </div>
        </div>

        <!-- Conteúdo principal -->
        <div class="failed-main">
          <div class="status-icon">
            <v-icon size="72" :color="getStatusIconColor(paylink.status)">{{ getStatusIcon(paylink.status) }}</v-icon>
          </div>
          
          <h1 class="failed-title">{{ getStatusTitle(paylink.status) }}</h1>
          
          <p class="failed-message">{{ paylink.status_message }}</p>
          
          <!-- Card com ações -->
          <div class="action-card">
            <div class="action-header">
              <v-icon color="#5f27cd" class="mr-2">help_outline</v-icon>
              <span class="action-title">O que você pode fazer:</span>
            </div>
            
            <div class="action-list">
              <div v-for="action in getStatusActions(paylink.status)" :key="action.icon" class="action-item">
                <v-icon :color="action.color" size="20" class="mr-3">{{ action.icon }}</v-icon>
                <span v-html="action.text"></span>
              </div>
            </div>
          </div>
          
          <!-- Botão de ação -->
          <div class="failed-actions">
            <v-btn 
              v-if="paylink.status === 'failed'"
              large 
              :color="paylink.system.primary_color || '#5f27cd'"
              elevation="2"
              rounded
              @click="loadPaylink"
              class="retry-btn"
            >
              <v-icon left>refresh</v-icon>
              Tentar Novamente
            </v-btn>
            
            <v-btn 
              v-else
              large 
              :color="paylink.system.primary_color || '#5f27cd'"
              elevation="2"
              rounded
              @click="goBack"
              class="retry-btn"
            >
              <v-icon left>arrow_back</v-icon>
              Voltar
            </v-btn>
          </div>
        </div>
      </div>
    </div>



    <!-- Payment Form -->
    <div v-else-if="paylink && paylink.is_available" class="payment-container">
      <div class="payment-page loaded">
      <!-- Header -->
      <div class="payment-header">
        <div class="d-flex align-center justify-center mb-6">
          <img
            v-if="paylink.system.logo_url"
            :src="paylink.system.logo_url"
            :alt="paylink.system.app_name"
            class="company-logo mr-4"
          >
          <div>
            <h1 class="text-h4 font-weight-bold">
              {{ paylink.system.app_name }}
            </h1>
          </div>
        </div>
      </div>

      <!-- Payment Status Messages -->
      <div v-if="paylink.is_paid" class="mb-6">
        <v-alert
          type="success"
          prominent
          outlined
          icon="check_circle"
        >
          <div class="text-h6">Pagamento já realizado!</div>
          <div v-if="paylink.payment">
            <p><strong>Código de Autorização:</strong> {{ paylink.payment.authorization_code }}</p>
            <p><strong>Data do Pagamento:</strong> {{ paylink.payment.paid_at }}</p>
          </div>
        </v-alert>
      </div>

      <div v-else-if="paylink.is_expired" class="mb-6">
        <v-alert
          type="warning"
          prominent
          outlined
          icon="schedule"
        >
          <div class="text-h6">Pagamento Vencido</div>
          <div>Este link de pagamento expirou em {{ formatDate(paylink.duedate) }}</div>
        </v-alert>
      </div>

      <div v-else-if="!paylink.is_available" class="mb-6">
        <v-alert
          :type="getAlertType(paylink.status)"
          prominent
          outlined
          :icon="getAlertIcon(paylink.status)"
        >
          <div class="text-h6">{{ paylink.status_message }}</div>
          <div v-if="paylink.status === 'cancelled'">
            Este pagamento foi cancelado e não pode mais ser processado.
          </div>
          <div v-else-if="paylink.status === 'failed'">
            Este pagamento falhou e não pode ser processado novamente.
          </div>
          <div v-else-if="paylink.status === 'downed'">
            Este pagamento está temporariamente indisponível.
          </div>
          <div v-else-if="paylink.status === 'completed'">
            Este pagamento já foi finalizado.
          </div>
          <div v-else-if="paylink.status === 'approved'">
            Este pagamento já foi aprovado.
          </div>
          <div v-else>
            Este pagamento não está disponível para processamento.
          </div>
        </v-alert>
      </div>

      <!-- Payment Form -->
      <div v-if="paylink.is_available">
        <v-row class="payment-cards-row">
          <!-- Payment Details Card -->
          <v-col cols="12" md="5" class="payment-details-col">
            <v-card class="payment-details-card" outlined>
              <v-card-title class="primary white--text">
                <v-icon left color="white">receipt</v-icon>
                Detalhes do Pagamento
              </v-card-title>
              <v-card-text class="pa-6">
                <div class="payment-detail">
                  <span class="detail-label">Descrição:</span>
                  <span class="detail-value">{{ paylink.description }}</span>
                </div>
                <div class="payment-detail">
                  <span class="detail-label">Valor:</span>
                  <span class="detail-value text-h6 success--text font-weight-bold">
                    R$ {{ formatCurrency(paylink.amount) }}
                  </span>
                </div>
                <div class="payment-detail">
                  <span class="detail-label">Vencimento:</span>
                  <span class="detail-value">{{ formatDate(paylink.duedate) }}</span>
                </div>
                <div class="payment-detail">
                  <span class="detail-label">Cliente:</span>
                  <span class="detail-value">{{ paylink.name }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Payment Form Card -->
          <v-col cols="12" md="7" class="payment-form-col">
            <v-card class="payment-form-card" outlined>
              <v-card-title class="success white--text">
                <v-icon left color="white">credit_card</v-icon>
                Dados do Cartão de Crédito
              </v-card-title>
              <v-card-text class="pa-6">
            <v-form
              ref="paymentForm"
              v-model="formValid"
              @submit.prevent="processPayment"
              autocomplete="on"
            >
              <div class="payment-form-content">
                <!-- Card Number -->
                <div class="form-field-group">
                  <label class="form-label">
                    <v-icon small class="mr-2">credit_card</v-icon>
                    Número do Cartão
                  </label>
                  <div class="card-number-container">
                    <v-text-field
                      v-model="form.pan"
                      placeholder="1234 5678 9012 3456"
                      :rules="[rules.required, rules.cardNumber]"
                      maxlength="19"
                      @input="formatCardNumber"
                      autocomplete="cc-number"
                      required
                      outlined
                      dense
                      hide-details="auto"
                      class="card-number-field"
                    >
                      <template v-slot:append>
                        <div class="card-brand-indicator">
                          <img
                            v-if="detectedBrand && detectedBrand.image"
                            :src="detectedBrand.image"
                            :alt="detectedBrand.name"
                            class="card-brand-image"
                          />
                          <v-chip
                            v-else-if="detectedBrand"
                            :color="detectedBrand.color"
                            x-small
                            class="brand-chip"
                          >
                            <v-icon x-small left>{{ detectedBrand.icon }}</v-icon>
                            {{ detectedBrand.name }}
                          </v-chip>
                        </div>
                      </template>
                    </v-text-field>
                  </div>
                </div>

                <!-- Card Holder Name -->
                <div class="form-field-group">
                  <label class="form-label">
                    <v-icon small class="mr-2">person</v-icon>
                    Nome no Cartão
                  </label>
                  <v-text-field
                    v-model="form.cardHolderName"
                    placeholder="JOÃO SILVA"
                    :rules="[rules.required, rules.cardHolderName]"
                    autocomplete="cc-name"
                    required
                    outlined
                    dense
                    hide-details="auto"
                    style="text-transform: uppercase"
                    @input="formatCardHolderName"
                  ></v-text-field>
                </div>

                <!-- Expiry and CVV Row -->
                <div class="form-row">
                  <div class="form-field-group expiry-group">
                    <label class="form-label">
                      <v-icon small class="mr-2">event</v-icon>
                      Vencimento
                      <v-chip
                        v-if="isCardExpired"
                        color="error"
                        x-small
                        class="ml-2"
                      >
                        <v-icon x-small left>warning</v-icon>
                        Expirado
                      </v-chip>
                    </label>
                    <div class="expiry-container">
                      <v-select
                        v-model="form.expiryMonth"
                        :items="paylink.payment_options.months"
                        placeholder="Mês"
                        :rules="[rules.required]"
                        autocomplete="cc-exp-month"
                        required
                        outlined
                        dense
                        hide-details="auto"
                        class="expiry-month"
                        @change="validateExpiryDate"
                      ></v-select>
                      <v-select
                        v-model="form.expiryYear"
                        :items="paylink.payment_options.years"
                        placeholder="Ano"
                        :rules="[rules.required]"
                        autocomplete="cc-exp-year"
                        required
                        outlined
                        dense
                        hide-details="auto"
                        class="expiry-year"
                        @change="validateExpiryDate"
                      ></v-select>
                    </div>
                  </div>

                  <div class="form-field-group cvv-group">
                    <label class="form-label">
                      <v-icon small class="mr-2">lock</v-icon>
                      CVV
                    </label>
                    <v-text-field
                      v-model="form.securityCode"
                      placeholder="123"
                      :rules="[rules.required, rules.cvv]"
                      maxlength="4"
                      autocomplete="cc-csc"
                      type="password"
                      required
                      outlined
                      dense
                      hide-details="auto"
                      @input="formatCvv"
                    ></v-text-field>
                  </div>
                </div>

                <!-- Payment Options and Total -->
                <div class="form-row">
                  <div class="form-field-group payment-option-group">
                    <label class="form-label">
                      <v-icon small class="mr-2">payment</v-icon>
                      Forma de Pagamento
                    </label>
                    <v-select
                      v-model="form.paymentOptionCode"
                      :items="installmentOptions"
                      :rules="[rules.required]"
                      required
                      outlined
                      dense
                      hide-details="auto"
                    ></v-select>
                  </div>

                  <div class="form-field-group total-group">
                    <label class="form-label">
                      <v-icon small class="mr-2">monetization_on</v-icon>
                      Valor Total
                    </label>
                    <div class="total-amount-display">
                      <v-icon color="success" class="mr-2">monetization_on</v-icon>
                      <span class="total-amount">R$ {{ formatCurrency(paylink.amount) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="payment-actions">
                <v-btn
                  type="submit"
                  color="success"
                  large
                  :loading="processing"
                  :disabled="!formValid || processing"
                  class="payment-button"
                  elevation="2"
                >
                  <v-icon left>credit_card</v-icon>
                  {{ processing ? 'Processando...' : 'Finalizar Pagamento' }}
                </v-btn>
              </div>
            </v-form>
          </v-card-text>
        </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Security Information -->
      <v-card class="mt-6" color="grey lighten-5" flat>
        <v-card-text class="text-center">
          <v-icon color="success" size="24" class="mr-2">security</v-icon>
          <span class="text-body-2 grey--text text--darken-2">
            Seus dados estão protegidos com criptografia SSL de 256 bits
          </span>
        </v-card-text>
      </v-card>
      </div>
    </div>

    <!-- Payment Receipt -->
    <div v-if="showReceipt" class="receipt-container">
      <PaymentReceipt
        :paylink="paylink"
        :payment-result="paymentResult"
        @download-pdf="downloadPDF"
        @close-receipt="showReceipt = false"
      />
    </div>

    <!-- Success Dialog (Fallback) -->
    <v-dialog v-model="successDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="success white--text text-center">
          <v-icon left color="white" size="32">check_circle</v-icon>
          Pagamento Realizado!
        </v-card-title>
        <v-card-text class="text-center pa-6">
          <v-icon color="success" size="80" class="mb-4">check_circle</v-icon>
          <h3 class="text-h5 mb-4">Pagamento processado com sucesso!</h3>
          <div v-if="paymentResult">
            <p><strong>Código de Autorização:</strong> {{ paymentResult.authorization_code }}</p>
            <p><strong>Valor:</strong> R$ {{ formatCurrency(paymentResult.amount) }}</p>
          </div>
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn color="success" large @click="showReceipt = true; successDialog = false">
            Ver Comprovante
          </v-btn>
          <v-btn color="primary" outlined large @click="successDialog = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" max-width="500">
      <v-card>
        <v-card-title class="error white--text">
          <v-icon left color="white">error</v-icon>
          Erro no Pagamento
        </v-card-title>
        <v-card-text class="pa-6">
          <p>{{ paymentError }}</p>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="error" text @click="errorDialog = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import Flowit from "@/services/flowit";
import PaymentReceipt from "./PaymentReceipt.vue";

export default {
  name: "PaymentPage",
  components: {
    PaymentReceipt,
  },
  props: {
    uuid: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      error: null,
      paylink: null,
      processing: false,
      formValid: false,
      successDialog: false,
      errorDialog: false,
      paymentError: "",
      paymentResult: null,
      showReceipt: false,

      form: {
        pan: "",
        cardHolderName: "",
        expiryMonth: "",
        expiryYear: "",
        securityCode: "",
        paymentOptionCode: "SINGLE",
      },

      rules: {
        required: value => !!value || "Campo obrigatório",
        cardNumber: (value) => {
          const cleaned = value.replace(/\s/g, "");
          return (cleaned.length >= 13 && cleaned.length <= 19) || "Número de cartão inválido";
        },
        cvv: value => /^\d{3,4}$/.test(value) || "CVV deve ter 3 ou 4 dígitos",
        cardHolderName: (value) => {
          if (!value || value.trim().length === 0) {
            return "Nome do titular é obrigatório";
          }
          if (value.trim().length < 3) {
            return "Nome deve ter pelo menos 3 caracteres";
          }
          if (value.trim().length > 50) {
            return "Nome deve ter no máximo 50 caracteres";
          }
          // Validar se contém apenas letras, espaços e caracteres especiais comuns
          const nameRegex = /^[a-zA-ZÀ-ÿ\s]+$/;
          if (!nameRegex.test(value.trim())) {
            return "Nome deve conter apenas letras";
          }
          return true;
        },
        expiryDate: () => {
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth() + 1; // getMonth() retorna 0-11
          
          const selectedMonth = parseInt(this.form.expiryMonth);
          const selectedYear = parseInt(this.form.expiryYear);
          
          if (!selectedMonth || !selectedYear) {
            return "Data de vencimento obrigatória";
          }
          
          if (selectedYear < currentYear || (selectedYear === currentYear && selectedMonth < currentMonth)) {
            return "Cartão expirado";
          }
          
          return true;
        },
      },
    };
  },

  computed: {
    installmentOptions() {
      if (!this.paylink) return [];

      const options = [
        { text: "À vista", value: "SINGLE" },
      ];

      for (let i = 2; i <= this.paylink.installment_limit; i += 1) {
        options.push({
          text: `${i}x sem juros`,
          value: `INSTALLMENT_${i}`,
        });
      }

      return options;
    },

    detectedBrand() {
      const cardNumber = this.form.pan.replace(/\s/g, "");

      // Mostrar bandeira assim que o usuário digitar o primeiro dígito
      if (cardNumber.length === 0) return null;

      const brands = {
        visa: {
          pattern: /^4/,
          name: "Visa",
          icon: "credit_card",
          color: "primary",
          image: require("@/components/desktop/brand/svg/visa.svg"),
        },
        mastercard: {
          pattern: /^5[1-5]|^2[2-7]/,
          name: "Mastercard",
          icon: "credit_card",
          color: "orange",
          image: require("@/components/desktop/brand/svg/mastercard.svg"),
        },
        amex: {
          pattern: /^3[47]/,
          name: "American Express",
          icon: "credit_card",
          color: "green",
          image: require("@/components/desktop/brand/svg/amex.svg"),
        },
        elo: {
          pattern: /^(636368|438935|504175|451416|636297|5067|4576|4011|627780|65)/,
          name: "Elo",
          icon: "credit_card",
          color: "yellow darken-2",
          image: require("@/components/desktop/brand/svg/elo.png"),
        },
        hipercard: {
          pattern: /^(606282|637095|637568|637599|637609|637612)/,
          name: "Hipercard",
          icon: "credit_card",
          color: "red",
          image: null, // Hipercard não tem imagem disponível
        },
        maestro: {
          pattern: /^(5018|5020|5038|6304|6759|6761|6763)/,
          name: "Maestro",
          icon: "credit_card",
          color: "blue",
          image: require("@/components/desktop/brand/svg/maestro.svg"),
        },
      };

      // Detectar bandeira baseado nos primeiros dígitos
      const found = Object.values(brands).find(brand => brand.pattern.test(cardNumber));
      return found || null;
    },

    isCardExpired() {
      if (!this.form.expiryMonth || !this.form.expiryYear) {
        return false;
      }
      
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      
      const selectedMonth = parseInt(this.form.expiryMonth);
      const selectedYear = parseInt(this.form.expiryYear);
      
      return selectedYear < currentYear || (selectedYear === currentYear && selectedMonth < currentMonth);
    },

  },
  
  async mounted() {
    try {
      await this.loadPaylink();
    } catch (error) {
      console.error('Error in mounted:', error);
      this.error = 'Erro ao carregar página de pagamento';
      this.loading = false;
    }
  },

  errorCaptured(err, vm, info) {
    console.error('Vue Error Captured:', err, info);
    return false;
  },

  watch: {
    paylink: {
      handler(newPaylink) {
        if (newPaylink && newPaylink.is_paid && newPaylink.payment) {
          // Se o pagamento já foi pago, mostrar o comprovante automaticamente
          this.paymentResult = newPaylink.payment;
          this.showReceipt = true;
        }
      },
      immediate: true
    }
  },

  beforeDestroy() {
    // Restore default title when leaving the page
    document.title = "FlowIt";
    
    // Clean up custom CSS properties
    document.documentElement.style.removeProperty('--paylink-primary-color');
    document.documentElement.style.removeProperty('--paylink-secondary-color');
    document.documentElement.style.removeProperty('--paylink-primary-transparent');
    document.documentElement.style.removeProperty('--paylink-primary-dark');
  },

  
  methods: {
    async loadPaylink() {
      console.log('!!!!! LOADPAYLINK METHOD CALLED - VERSION 2 !!!!!');
      try {
        this.loading = true;
        this.error = null;
        
        const response = await Flowit.paylinks.getPublicPaylink(this.uuid);
        this.paylink = response.data.paylink;
        
        console.log('=== PAYLINK DATA LOADED ===');
        console.log('Paylink status:', this.paylink.status);
        console.log('Is invalid:', this.paylink.is_invalid);
        console.log('Is available:', this.paylink.is_available);
        console.log('Is paid:', this.paylink.is_paid);
        console.log('Is expired:', this.paylink.is_expired);
        console.log('Status message:', this.paylink.status_message);
        console.log('Full paylink data:', this.paylink);
        console.log('===========================');
        
        // Se o paylink tem status inválido, garantir que error seja null para mostrar a mensagem correta
        if (this.paylink.is_invalid || (this.paylink.status && this.paylink.status !== 'waiting')) {
          this.error = null;
        }
        
        
        // Set default form values
        if (this.paylink.is_available) {
          this.form.amount = this.paylink.amount;
        }
        
        // Se o pagamento já foi pago, mostrar o comprovante automaticamente
        if (this.paylink.is_paid && this.paylink.payment) {
          this.paymentResult = this.paylink.payment;
          this.showReceipt = true;
        }
        
        // Update document title and favicon with partner branding
        this.updatePageBranding();
        
        // Update page colors with partner branding
        this.updatePageColors();
        
        // Garantir que loading seja false após sucesso
        this.loading = false;
        
        // Wait for Vue to update the DOM
        await this.$nextTick();
        
        // Force Vue to re-render
        this.$forceUpdate();
        
      } catch (error) {
        console.error('Error loading paylink:', error);
        
        if (error.response && error.response.data) {
          const responseData = error.response.data;
          
          // Se recebemos um erro 422, provavelmente é do verify_paylink_status
          if (error.response.status === 422) {
            // Extrair o status da mensagem de erro ou usar um padrão
            let status = responseData.status || 'unavailable';
            let statusMessage = responseData.error || 'Este pagamento não está disponível';
            
            // Tentar identificar o status pela mensagem de erro
            if (responseData.error) {
              if (responseData.error.includes('cancelado')) {
                status = 'cancelled';
              } else if (responseData.error.includes('falhou')) {
                status = 'failed';
              } else if (responseData.error.includes('indisponível')) {
                status = 'downed';
              } else if (responseData.error.includes('finalizado')) {
                status = 'completed';
              } else if (responseData.error.includes('aprovado')) {
                status = 'approved';
              } else if (responseData.error.includes('processado')) {
                status = 'completed';
              } else if (responseData.error.includes('vencido')) {
                status = 'expired';
              }
            }
            
            // Criar um objeto paylink mínimo para exibir o status correto
            this.paylink = {
              status: status,
              status_message: statusMessage,
              is_invalid: true,
              is_paid: responseData.is_paid || false,
              is_expired: responseData.is_expired || false,
              is_available: false,
              system: {
                app_name: 'FlowIt',
                logo_url: '/logo_flowit.png',
                primary_color: '#1976d2',
                secondary_color: '#ffffff'
              }
            };
            
            // Não mostrar erro adicional, pois já temos a mensagem
            this.error = null;
            
            // Atualizar cores da página mesmo com erro
            this.updatePageBranding();
            this.updatePageColors();
          } else if (responseData.is_invalid || responseData.status) {
            // Caso o backend já esteja retornando os dados corretos
            this.paylink = {
              status: responseData.status,
              status_message: responseData.error || this.getStatusMessage(responseData.status),
              is_invalid: true,
              is_paid: responseData.is_paid || false,
              is_expired: responseData.is_expired || false,
              is_available: false,
              system: {
                app_name: 'FlowIt',
                logo_url: '/logo_flowit.png',
                primary_color: '#1976d2',
                secondary_color: '#ffffff'
              }
            };
            
            this.error = null;
            this.updatePageBranding();
            this.updatePageColors();
          } else {
            // Outros erros
            this.error = responseData.error || 'Erro ao carregar dados do pagamento';
          }
        } else {
          // Erro de rede ou outro erro não tratado
          this.error = 'Erro ao conectar com o servidor. Por favor, tente novamente.';
        }
      } finally {
        this.loading = false;
      }
    },
    
    async processPayment() {
      if (!this.$refs.paymentForm.validate()) {
        return;
      }

      // Validar data de vencimento do cartão
      if (this.isCardExpired) {
        this.paymentError = "Cartão expirado. Por favor, verifique a data de vencimento.";
        this.errorDialog = true;
        return;
      }
      
      try {
        this.processing = true;
        
        // Process payment option code to send correct format
        let installments = 1;
        if (this.form.paymentOptionCode !== "SINGLE") {
          installments = parseInt(this.form.paymentOptionCode.replace("INSTALLMENT_", ""));
        }
        
        // Validate installments
        if (installments < 1 || installments > this.paylink.installment_limit) {
          this.paymentError = `Número de parcelas inválido. Deve ser entre 1 e ${this.paylink.installment_limit}.`;
          this.errorDialog = true;
          this.processing = false;
          return;
        }
        
        const paymentData = {
          ...this.form,
          amount: this.paylink.amount,
          pan: this.form.pan.replace(/\s/g, ''), // Remove spaces from card number
          installments: installments // Send numeric installments
        };
        
        const response = await Flowit.paylinks.processPayment(this.uuid, paymentData);
        
        if (response.data.success) {
          this.paymentResult = response.data.payment;
          this.showReceipt = true;
          
          // Reload paylink data to show paid status
          await this.loadPaylink();
        } else {
          this.paymentError = response.data.message || 'Erro no processamento do pagamento';
          this.errorDialog = true;
        }
        
      } catch (error) {
        console.error('Payment processing error:', error);
        
        const errorMessage = (error.response && error.response.data && error.response.data.message) || 
                           (error.response && error.response.data && error.response.data.error) || 
                           'Erro ao processar pagamento';
        
        this.paymentError = errorMessage;
        this.errorDialog = true;
      } finally {
        this.processing = false;
      }
    },
    
    formatCardNumber() {
      // Add spaces every 4 digits
      let value = this.form.pan.replace(/\s/g, '');
      value = value.replace(/(.{4})/g, '$1 ').trim();
      this.form.pan = value;
    },
    
    formatCardHolderName() {
      // Convert to uppercase
      this.form.cardHolderName = this.form.cardHolderName.toUpperCase();
    },
    
    formatCvv() {
      // Only allow numbers
      this.form.securityCode = this.form.securityCode.replace(/\D/g, '');
    },

    validateExpiryDate() {
      if (this.form.expiryMonth && this.form.expiryYear) {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        
        const selectedMonth = parseInt(this.form.expiryMonth);
        const selectedYear = parseInt(this.form.expiryYear);
        
        if (selectedYear < currentYear || (selectedYear === currentYear && selectedMonth < currentMonth)) {
          this.$nextTick(() => {
            this.$refs.paymentForm.validate();
          });
        }
      }
    },
    
    formatCurrency(value) {
      return parseFloat(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(date) {
      if (!date) return 'N/A';
      
      // Se é um objeto com campos padronizados da API, usar display
      if (typeof date === 'object' && date.display) {
        return date.display;
      }
      
      // Se é um objeto com campo iso, converter
      if (typeof date === 'object' && date.iso) {
        try {
          return new Date(date.iso).toLocaleDateString('pt-BR');
        } catch (error) {
          console.warn('Erro ao converter data ISO:', date.iso, error);
          return date.iso;
        }
      }
      
      // Se é string em formato brasileiro, usar diretamente
      if (typeof date === 'string' && date.includes('/')) {
        return date;
      }
      
      // Fallback: tentar converter (pode causar Invalid Date)
      try {
        return new Date(date).toLocaleDateString('pt-BR');
      } catch (error) {
        console.warn('Erro ao formatar data:', date, error);
        return date || 'N/A';
      }
    },

    getAlertType(status) {
      switch (status) {
        case 'cancelled':
          return 'error';
        case 'failed':
          return 'error';
        case 'downed':
          return 'warning';
        case 'completed':
          return 'info';
        case 'approved':
          return 'success';
        default:
          return 'warning';
      }
    },

    getAlertIcon(status) {
      switch (status) {
        case 'cancelled':
          return 'cancel';
        case 'failed':
          return 'error';
        case 'downed':
          return 'warning';
        case 'completed':
          return 'check_circle';
        case 'approved':
          return 'check_circle';
        default:
          return 'info';
      }
    },

    updatePageBranding() {
      if (!this.paylink) return;

      // Update document title with partner name
      const partnerName = this.paylink.account.fantasy_name || this.paylink.account.name;
      document.title = `Pagamento - ${partnerName}`;

      // Update favicon if partner logo is available
      if (this.paylink.system.logo_url) {
        this.updateFavicon(this.paylink.system.logo_url);
      }
    },

    updateFavicon(logoUrl) {
      // Remove existing favicon
      const existingFavicon = document.querySelector('link[rel*="icon"]');
      if (existingFavicon) {
        existingFavicon.remove();
      }

      // Add new favicon with partner logo
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/png';
      favicon.href = logoUrl;
      document.head.appendChild(favicon);

      // Also update apple-touch-icon
      const existingAppleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (existingAppleIcon) {
        existingAppleIcon.href = logoUrl;
      } else {
        const appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        appleIcon.href = logoUrl;
        document.head.appendChild(appleIcon);
      }
    },

    updatePageColors() {
      if (!this.paylink || !this.paylink.system.primary_color) return;

      const primaryColor = this.paylink.system.primary_color;
      const secondaryColor = this.paylink.system.secondary_color || '#ffffff';

      // Create transparent versions of the primary color
      const primaryColorTransparent = this.hexToRgba(primaryColor, 0.1);
      const darkerShadeTransparent = this.hexToRgba(this.darkenColor(primaryColor, 20), 0.05);
      const primaryColorDark = this.darkenColor(primaryColor, 30);

      // Apply CSS custom properties for dynamic theming
      document.documentElement.style.setProperty('--paylink-primary-color', primaryColor);
      document.documentElement.style.setProperty('--paylink-secondary-color', secondaryColor);
      document.documentElement.style.setProperty('--paylink-primary-transparent', primaryColorTransparent);
      document.documentElement.style.setProperty('--paylink-primary-dark', primaryColorDark);

      // Update the payment page background gradient with transparency
      const paymentPageElement = document.querySelector('.payment-page');
      if (paymentPageElement) {
        paymentPageElement.style.background = `linear-gradient(135deg, ${primaryColorTransparent} 0%, ${darkerShadeTransparent} 100%)`;
      }

      // Update the failed container background if it exists
      const failedContainer = document.querySelector('.failed-container');
      if (failedContainer) {
        failedContainer.style.background = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColorDark} 100%)`;
      }
    },

    darkenColor(color, percent) {
      // Convert hex to RGB
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      // Darken each component
      const darkenedR = Math.max(0, Math.floor(r * (100 - percent) / 100));
      const darkenedG = Math.max(0, Math.floor(g * (100 - percent) / 100));
      const darkenedB = Math.max(0, Math.floor(b * (100 - percent) / 100));

      // Convert back to hex
      const toHex = (n) => {
        const hex = n.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      };

      return `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`;
    },

    hexToRgba(hex, alpha) {
      // Convert hex to RGB
      const hexColor = hex.replace('#', '');
      const r = parseInt(hexColor.substr(0, 2), 16);
      const g = parseInt(hexColor.substr(2, 2), 16);
      const b = parseInt(hexColor.substr(4, 2), 16);
      
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    getStatusMessage(status) {
      switch (status) {
        case 'waiting':
          return "Aguardando pagamento";
        case 'cancelled':
          return "Pagamento cancelado";
        case 'failed':
          return "Pagamento falhou";
        case 'downed':
          return "Pagamento indisponível";
        case 'completed':
          return "Pagamento finalizado";
        case 'approved':
          return "Pagamento aprovado";
        case 'processing':
          return "Pagamento em processamento";
        case 'scheduled':
          return "Pagamento agendado";
        case 'anticipated':
          return "Pagamento antecipado";
        default:
          return "Status desconhecido";
      }
    },

    getStatusTitle(status) {
      switch (status) {
        case 'cancelled':
          return "Pagamento Cancelado";
        case 'failed':
          return "Pagamento Indisponível";
        case 'expired':
          return "Pagamento Vencido";
        case 'completed':
          return "Pagamento Finalizado";
        case 'approved':
          return "Pagamento Aprovado";
        case 'suspended':
          return "Pagamento Suspenso";
        default:
          return "Pagamento Indisponível";
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case 'cancelled':
          return 'cancel';
        case 'failed':
          return 'error';
        case 'expired':
          return 'schedule';
        case 'completed':
          return 'check_circle';
        case 'approved':
          return 'check_circle';
        case 'suspended':
          return 'pause_circle';
        default:
          return 'error';
      }
    },

    getStatusIconColor(status) {
      switch (status) {
        case 'cancelled':
          return '#ff4757';
        case 'failed':
          return '#ff4757';
        case 'expired':
          return '#ffa502';
        case 'completed':
          return '#2ed573';
        case 'approved':
          return '#2ed573';
        case 'suspended':
          return '#ffa502';
        default:
          return '#ff4757';
      }
    },

    getStatusActions(status) {
      const actions = [];
      
      switch (status) {
        case 'cancelled':
          actions.push(
            {
              icon: 'info',
              color: '#3742fa',
              text: 'Este link de pagamento foi cancelado e não pode mais ser processado.'
            },
            {
              icon: 'contact_support',
              color: '#3742fa',
              text: `Entre em contato com <strong>${this.paylink.account.fantasy_name || this.paylink.account.name}</strong> para mais informações.`
            }
          );
          break;
          
        case 'failed':
          actions.push(
            {
              icon: 'schedule',
              color: '#10ac84',
              text: 'Aguarde alguns minutos e tente novamente'
            },
            {
              icon: 'link',
              color: '#ff6b6b',
              text: 'Verifique se o link de pagamento está correto'
            },
            {
              icon: 'contact_support',
              color: '#3742fa',
              text: `Entre em contato com <strong>${this.paylink.account.fantasy_name || this.paylink.account.name}</strong> se o problema persistir`
            }
          );
          break;
          
        case 'expired':
          actions.push(
            {
              icon: 'schedule',
              color: '#ffa502',
              text: 'Este link de pagamento expirou e não pode mais ser processado.'
            },
            {
              icon: 'contact_support',
              color: '#3742fa',
              text: `Entre em contato com <strong>${this.paylink.account.fantasy_name || this.paylink.account.name}</strong> para solicitar um novo link.`
            }
          );
          break;
          
        case 'completed':
        case 'approved':
          actions.push(
            {
              icon: 'check_circle',
              color: '#2ed573',
              text: 'Este pagamento já foi processado com sucesso.'
            },
            {
              icon: 'contact_support',
              color: '#3742fa',
              text: `Entre em contato com <strong>${this.paylink.account.fantasy_name || this.paylink.account.name}</strong> se precisar do comprovante.`
            }
          );
          break;
          
        default:
          actions.push(
            {
              icon: 'contact_support',
              color: '#3742fa',
              text: `Entre em contato com <strong>${this.paylink.account.fantasy_name || this.paylink.account.name}</strong> para mais informações.`
            }
          );
      }
      
      return actions;
    },

    goBack() {
      // Voltar para a página anterior ou fechar a aba
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    },

    downloadPDF() {
      // Implementar download de PDF
      console.log('Download PDF functionality to be implemented');
      this.$emit('download-pdf');
    }
  }
};
</script>

<style scoped>
.payment-page {
  min-height: 100vh;
  background: #ffffff;
  padding: 40px 20px;
}

.payment-page.loaded {
  background: #ffffff;
}

.loading-container,
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
}

.payment-container {
  max-width: 1200px;
  margin: 0 auto;
  background: transparent;
  border-radius: 16px;
  padding: 24px;
}

.payment-cards-row {
  align-items: stretch;
  display: flex;
  flex-wrap: nowrap;
}

.payment-details-col {
  display: flex;
  flex-direction: column;
  flex: 0 0 41.666667%;
  max-width: 41.666667%;
  padding-right: 16px;
}

.payment-form-col {
  display: flex;
  flex-direction: column;
  flex: 0 0 58.333333%;
  max-width: 58.333333%;
  padding-left: 16px;
}

.payment-details-card,
.payment-form-card {
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.payment-header {
  text-align: center;
  margin-bottom: 2rem;
  color: #333;
}

.company-logo {
  max-height: 80px;
  max-width: 200px;
  object-fit: contain;
}

.payment-detail {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.detail-label {
  font-weight: 500;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.detail-value {
  font-weight: 600;
  color: #333;
}

.v-card {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.v-btn {
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

.v-text-field, .v-select {
  margin-bottom: 0.5rem;
}

/* Brand detection animations */
.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: scale(1.05);
}

/* Modern Payment Form Styling */
.payment-form-content {
  padding: 0;
}

.form-field-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: #37474f;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.form-label .v-icon {
  color: #666;
  margin-right: 8px;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.expiry-group {
  flex: 2;
}

.cvv-group {
  flex: 1;
}

.payment-option-group {
  flex: 2;
}

.total-group {
  flex: 1;
}

.expiry-container {
  display: flex;
  gap: 8px;
}

.expiry-month {
  flex: 1;
}

.expiry-year {
  flex: 1;
}

.card-number-container {
  position: relative;
}

.card-brand-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 8px;
}

.brand-chip {
  font-size: 0.7rem !important;
  height: 20px !important;
}

.card-brand-image {
  height: 24px;
  width: auto;
  max-width: 40px;
  object-fit: contain;
  margin-right: 8px;
  animation: slideInBrand 0.3s ease;
}

@keyframes slideInBrand {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.total-amount-display {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px 16px;
  text-align: center;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.total-amount {
  font-size: 1.125rem;
  font-weight: 600;
  color: #2e7d32;
}

.payment-actions {
  margin-top: 32px;
  text-align: center;
}

.payment-button {
  min-width: 280px !important;
  height: 48px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 8px !important;
  transition: all 0.3s ease !important;
}

.payment-button:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

/* Card number field styling */
.v-text-field input[autocomplete="cc-number"] {
  font-family: 'Courier New', monospace !important;
  letter-spacing: 1px;
}

/* CVV field styling */
.v-text-field input[autocomplete="cc-csc"] {
  font-family: 'Courier New', monospace !important;
  text-align: center;
  letter-spacing: 2px;
}

/* Enhanced card styling */
.v-card {
  transition: all 0.3s ease;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

/* Button hover effects */
.v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Focus states */
.v-text-field--focused .v-icon {
  color: var(--paylink-primary-color, var(--v-primary-base)) !important;
}

/* Success feedback */
.success--text {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* Brand chip animation */
.v-chip {
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Receipt Container */
.receipt-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--paylink-primary-color, #667eea) 0%, var(--paylink-primary-dark, #764ba2) 100%);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .payment-cards-row {
    flex-direction: column;
  }
  
  .payment-details-col,
  .payment-form-col {
    margin-bottom: 24px;
    flex: 0 0 100% !important;
    max-width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  
  .form-row {
    flex-direction: column;
    gap: 12px;
  }
  
  .expiry-container {
    gap: 12px;
  }
}

@media (max-width: 600px) {
  .payment-page {
    padding: 20px 10px;
  }

  .payment-container {
    padding: 16px;
    border-radius: 12px;
  }

  .company-logo {
    max-height: 60px;
    max-width: 150px;
  }

  .payment-header h1 {
    font-size: 1.5rem !important;
  }

  /* Prevent zoom on iOS */
  .v-text-field input {
    font-size: 16px !important;
  }
  
  .payment-details-card,
  .payment-form-card {
    margin-bottom: 16px;
  }
}

/* Estilos para tela de status inválido modernizada */
.failed-container {
  min-height: 100vh;
  background: linear-gradient(135deg, var(--paylink-primary-color, #667eea) 0%, var(--paylink-primary-dark, #764ba2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.failed-content {
  max-width: 600px;
  width: 100%;
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.failed-header {
  background: linear-gradient(135deg, #f8f9ff 0%, #e3e8ff 100%);
  padding: 32px 24px;
  text-align: center;
  border-bottom: 1px solid #e0e6ff;
}

.company-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.company-logo {
  max-height: 80px;
  max-width: 200px;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.company-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.failed-main {
  padding: 40px 32px;
  text-align: center;
}

.status-icon {
  margin-bottom: 24px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.failed-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 16px 0;
  letter-spacing: -0.5px;
}

.failed-message {
  font-size: 1.2rem;
  color: #4a5568;
  margin: 0 0 32px 0;
  line-height: 1.6;
}

.action-card {
  background: #f7fafc;
  border-radius: 16px;
  padding: 24px;
  margin: 32px 0;
  border: 1px solid #e2e8f0;
  text-align: left;
}

.action-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.action-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
}

.action-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.action-item {
  display: flex;
  align-items: center;
  font-size: 1rem;
  color: #4a5568;
  line-height: 1.5;
}

.failed-actions {
  margin-top: 32px;
}

.retry-btn {
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.retry-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Responsive para tela failed */
@media (max-width: 768px) {
  .failed-container {
    padding: 16px;
  }
  
  .failed-content {
    border-radius: 20px;
  }
  
  .failed-header {
    padding: 24px 20px;
  }
  
  .failed-main {
    padding: 32px 24px;
  }
  
  .failed-title {
    font-size: 2rem;
  }
  
  .failed-message {
    font-size: 1.1rem;
  }
  
  .action-card {
    padding: 20px;
    margin: 24px 0;
  }
  
  .company-logo {
    max-height: 60px;
    max-width: 160px;
  }
}

@media (max-width: 480px) {
  .failed-title {
    font-size: 1.75rem;
  }
  
  .action-item {
    font-size: 0.95rem;
  }
  
  .retry-btn {
    width: 100%;
  }
}
</style>
